import { z } from "zod";
import {
  API_LIMITS,
  assignmentVersionSchema,
  contentVersionSchema,
  courseCodeSchema,
  inviteCodeSchema,
  itemIdSchema,
  moduleIdSchema,
  reviewStateSchema,
  SCENE_IDS,
  sceneIdSchema,
  taskIdSchema,
  timestampSchema,
  uuidSchema,
} from "./primitives";

export const classroomStatusSchema = z.enum(["active", "archived"]);

export const createClassroomRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(API_LIMITS.className),
    courseCode: courseCodeSchema,
  })
  .strict();

export const classroomSchema = z
  .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(API_LIMITS.className),
    courseCode: courseCodeSchema,
    status: classroomStatusSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict();

export const createClassroomResponseSchema = z
  .object({
    classroom: classroomSchema,
    inviteCode: inviteCodeSchema,
  })
  .strict();

export const classroomListResponseSchema = z
  .object({ classrooms: z.array(classroomSchema).max(1_000) })
  .strict();

export const rotateInviteCodeResponseSchema = z
  .object({
    classroomId: uuidSchema,
    inviteCode: inviteCodeSchema,
    rotatedAt: timestampSchema,
  })
  .strict();

const publicCatalogItemSchema = z
  .object({
    id: itemIdSchema,
    text: z.string().trim().min(1).max(500),
  })
  .strict();

const publicTaskBaseShape = {
  id: taskIdSchema,
  order: z.number().int().min(1).max(API_LIMITS.formalTasks),
  sceneId: sceneIdSchema,
  moduleId: moduleIdSchema,
  title: z.string().trim().min(1).max(API_LIMITS.taskTitle),
  stem: z.string().trim().min(1).max(API_LIMITS.taskStem),
  reviewState: reviewStateSchema,
  selectable: z.boolean(),
  unavailableReason: z.string().trim().min(1).max(300).optional(),
} as const;

export const publicSingleChoiceTaskSchema = z
  .object({
    ...publicTaskBaseShape,
    type: z.literal("single_choice"),
    presentation: z.enum(["cards", "select"]),
    options: z.array(publicCatalogItemSchema).min(2).max(20),
  })
  .strict();

export const publicDragSortTaskSchema = z
  .object({
    ...publicTaskBaseShape,
    type: z.literal("drag_sort"),
    items: z.array(publicCatalogItemSchema).min(2).max(API_LIMITS.taskItems),
  })
  .strict();

export const publicDragMatchTaskSchema = z
  .object({
    ...publicTaskBaseShape,
    type: z.literal("drag_match"),
    left: z.array(publicCatalogItemSchema).min(2).max(API_LIMITS.taskItems),
    right: z.array(publicCatalogItemSchema).min(2).max(API_LIMITS.taskItems),
  })
  .strict();

export const publicTaskCatalogItemSchema = z
  .discriminatedUnion("type", [
    publicSingleChoiceTaskSchema,
    publicDragSortTaskSchema,
    publicDragMatchTaskSchema,
  ])
  .superRefine((task, context) => {
    if (task.selectable && task.reviewState !== "approved") {
      context.addIssue({
        code: "custom",
        path: ["selectable"],
        message: "Only approved tasks may be selectable.",
      });
    }
    if (!task.selectable && !task.unavailableReason) {
      context.addIssue({
        code: "custom",
        path: ["unavailableReason"],
        message: "A non-selectable task must explain why it is unavailable.",
      });
    }
    const collections =
      task.type === "single_choice"
        ? [task.options]
        : task.type === "drag_sort"
          ? [task.items]
          : [task.left, task.right];
    for (const collection of collections) {
      const ids = collection.map((item) => item.id);
      if (new Set(ids).size !== ids.length) {
        context.addIssue({
          code: "custom",
          message: "Catalog item IDs must be unique within each collection.",
        });
      }
    }
  });

export const taskCatalogResponseSchema = z
  .object({
    contentVersion: contentVersionSchema,
    tasks: z.array(publicTaskCatalogItemSchema).max(API_LIMITS.formalTasks),
    total: z.number().int().min(0).max(API_LIMITS.formalTasks),
  })
  .strict()
  .superRefine((catalog, context) => {
    const ids = catalog.tasks.map((task) => task.id);
    const orders = catalog.tasks.map((task) => task.order);
    if (catalog.total !== catalog.tasks.length) {
      context.addIssue({
        code: "custom",
        path: ["total"],
        message: "Total must match task count.",
      });
    }
    if (new Set(ids).size !== ids.length || new Set(orders).size !== orders.length) {
      context.addIssue({
        code: "custom",
        path: ["tasks"],
        message: "Task IDs and display orders must be unique.",
      });
    }
  });

export const assignmentItemSchema = z
  .object({
    position: z.number().int().min(1).max(API_LIMITS.assignmentItems),
    taskId: taskIdSchema,
    points: z.number().int().min(1).max(100),
  })
  .strict();

export const assignmentItemsSchema = z
  .array(assignmentItemSchema)
  .min(1)
  .max(API_LIMITS.assignmentItems)
  .superRefine((items, context) => {
    const taskIds = items.map((item) => item.taskId);
    const positions = items.map((item) => item.position);
    if (new Set(taskIds).size !== taskIds.length) {
      context.addIssue({ code: "custom", message: "Assignment task IDs must be unique." });
    }
    if (new Set(positions).size !== positions.length) {
      context.addIssue({ code: "custom", message: "Assignment positions must be unique." });
    }
    const expectedPositions = Array.from({ length: items.length }, (_, index) => index + 1);
    if (!expectedPositions.every((position) => positions.includes(position))) {
      context.addIssue({
        code: "custom",
        message: "Assignment positions must form a consecutive sequence starting at one.",
      });
    }
  });

export const createAssignmentDraftRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(API_LIMITS.assignmentTitle),
    contentVersion: contentVersionSchema,
    items: assignmentItemsSchema,
  })
  .strict();

export const updateAssignmentDraftRequestSchema = createAssignmentDraftRequestSchema
  .extend({ expectedVersion: assignmentVersionSchema })
  .strict();

const assignmentBaseShape = {
  id: uuidSchema,
  classroomId: uuidSchema,
  revisionId: uuidSchema,
  revisionNo: z.number().int().min(1).max(10_000),
  version: assignmentVersionSchema,
  title: z.string().trim().min(1).max(API_LIMITS.assignmentTitle),
  contentVersion: contentVersionSchema,
  items: assignmentItemsSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
} as const;

export const assignmentSchema = z.discriminatedUnion("status", [
  z.object({ ...assignmentBaseShape, status: z.literal("draft") }).strict(),
  z
    .object({
      ...assignmentBaseShape,
      status: z.literal("published"),
      publishedAt: timestampSchema,
    })
    .strict(),
  z
    .object({
      ...assignmentBaseShape,
      status: z.literal("withdrawn"),
      publishedAt: timestampSchema,
      withdrawnAt: timestampSchema,
      withdrawalReason: z.string().trim().min(1).max(300).optional(),
    })
    .strict(),
]);

export const assignmentListResponseSchema = z
  .object({ assignments: z.array(assignmentSchema).max(1_000) })
  .strict();

export const publishAssignmentRequestSchema = z
  .object({ expectedVersion: assignmentVersionSchema })
  .strict();

export const withdrawAssignmentRequestSchema = z
  .object({
    expectedVersion: assignmentVersionSchema,
    reason: z.string().trim().min(1).max(300).optional(),
  })
  .strict();

export const classroomResultsQuerySchema = z
  .object({
    cursor: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    assignmentRevisionId: uuidSchema.optional(),
  })
  .strict();

export const classroomTaskResultSchema = z
  .object({
    taskId: taskIdSchema,
    attemptCount: z.number().int().min(1),
    firstAttemptCorrect: z.boolean(),
    latestCorrect: z.boolean(),
    passed: z.boolean(),
    bestScore: z.number().min(0).max(1),
    latestScore: z.number().min(0).max(1),
    firstAttemptAt: timestampSchema,
    latestAttemptAt: timestampSchema,
    remediationStarted: z.boolean(),
    remediationCompleted: z.boolean(),
  })
  .strict();

export const teacherReportStatusSchema = z.enum(["not_started", "in_progress", "completed"]);

export const teacherReportRecommendationSchema = z
  .object({
    code: z.enum([
      "MODULE_NOT_STARTED",
      "SCENES_INCOMPLETE",
      "TASKS_UNATTEMPTED",
      "TASKS_NOT_PASSED",
      "FIRST_ATTEMPT_REVIEW",
      "REMEDIATION_REVIEW",
      "EVIDENCE_REHEARSAL",
    ]),
    message: z.string().trim().min(1).max(300),
    moduleId: moduleIdSchema.optional(),
    sceneIds: z.array(sceneIdSchema).max(18).optional(),
    taskIds: z.array(taskIdSchema).max(API_LIMITS.formalTasks).optional(),
  })
  .strict();

export const teacherReportTaskSchema = z
  .object({
    taskId: taskIdSchema,
    sceneId: sceneIdSchema,
    attempted: z.boolean(),
    passed: z.boolean(),
    attemptCount: z.number().int().min(0),
    firstAttemptCorrect: z.boolean().optional(),
    remediationStarted: z.boolean(),
    remediationCompleted: z.boolean(),
  })
  .strict();

export const teacherReportSceneSchema = z
  .object({
    sceneId: sceneIdSchema,
    status: teacherReportStatusSchema,
    eventCount: z.number().int().min(0),
    completed: z.boolean(),
    remediationStartedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    remediationCompletedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    sourceViewCount: z.number().int().min(0),
    contentViewCount: z.number().int().min(0),
    taskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    attemptedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    passedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    firstAttemptRecordedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    firstAttemptCorrectCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    retriedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    retryCount: z.number().int().min(0),
    attemptCount: z.number().int().min(0),
    tasks: z.array(teacherReportTaskSchema).max(API_LIMITS.formalTasks),
  })
  .strict();

export const teacherModuleReportSchema = z
  .object({
    moduleId: moduleIdSchema,
    status: teacherReportStatusSchema,
    completed: z.boolean(),
    completedSceneCount: z.number().int().min(0).max(18),
    sceneCount: z.number().int().min(1).max(18),
    attemptedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    taskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    passedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    firstAttemptRecordedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    firstAttemptCorrectCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    retriedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    retryCount: z.number().int().min(0),
    attemptCount: z.number().int().min(0),
    remediationStartedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    remediationCompletedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    sourceViewCount: z.number().int().min(0),
    contentViewCount: z.number().int().min(0),
    scenes: z.array(teacherReportSceneSchema).min(1).max(18),
    recommendations: z.array(teacherReportRecommendationSchema).min(1).max(6),
  })
  .strict();

export const teacherOverallReportSchema = z
  .object({
    status: teacherReportStatusSchema,
    completed: z.boolean(),
    completedModuleCount: z.number().int().min(0).max(6),
    moduleCount: z.literal(6),
    completedSceneCount: z.number().int().min(0).max(18),
    sceneCount: z.literal(18),
    attemptedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    taskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    passedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    firstAttemptRecordedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    firstAttemptCorrectCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    retriedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    retryCount: z.number().int().min(0),
    attemptCount: z.number().int().min(0),
    remediationStartedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    remediationCompletedCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
    sourceViewCount: z.number().int().min(0),
    contentViewCount: z.number().int().min(0),
    recommendations: z.array(teacherReportRecommendationSchema).min(1).max(6),
  })
  .strict();

export const teacherLearnerReportSchema = z
  .object({
    basis: z.enum(["server_authoritative", "local_current_session"]),
    scope: z.enum(["all_sessions", "assignment_revision", "current_local_session"]),
    modules: z.array(teacherModuleReportSchema).length(6),
    overall: teacherOverallReportSchema,
  })
  .strict();

export interface TeacherReportSceneFactInput {
  readonly sceneId: string;
  readonly completed: boolean;
  readonly eventCount: number;
  readonly remediationStartedCount: number;
  readonly remediationCompletedCount: number;
  readonly sourceViewCount: number;
  readonly contentViewCount: number;
}

export interface TeacherReportTaskCatalogInput {
  readonly taskId: string;
  readonly sceneId: string;
}

export interface TeacherReportTaskFactInput {
  readonly taskId: string;
  readonly attemptCount: number;
  readonly firstAttemptCorrect?: boolean;
  readonly passed: boolean;
  readonly remediationStarted: boolean;
  readonly remediationCompleted: boolean;
}

export interface BuildTeacherLearnerReportInput {
  readonly basis: "server_authoritative" | "local_current_session";
  readonly scope: "all_sessions" | "assignment_revision" | "current_local_session";
  readonly sceneFacts: readonly TeacherReportSceneFactInput[];
  readonly taskCatalog: readonly TeacherReportTaskCatalogInput[];
  readonly taskFacts: readonly TeacherReportTaskFactInput[];
}

function reportStatus(started: boolean, completed: boolean) {
  if (completed) return "completed" as const;
  return started ? ("in_progress" as const) : ("not_started" as const);
}

function moduleRecommendations(input: {
  readonly moduleId: string;
  readonly scenes: readonly z.infer<typeof teacherReportSceneSchema>[];
  readonly status: z.infer<typeof teacherReportStatusSchema>;
}) {
  const incompleteSceneIds = input.scenes
    .filter((scene) => !scene.completed)
    .map((scene) => scene.sceneId);
  const tasks = input.scenes.flatMap((scene) => scene.tasks);
  const unattemptedTaskIds = tasks.filter((task) => !task.attempted).map((task) => task.taskId);
  const notPassedTaskIds = tasks
    .filter((task) => task.attempted && !task.passed)
    .map((task) => task.taskId);
  const firstAttemptReviewTaskIds = tasks
    .filter((task) => task.attempted && task.firstAttemptCorrect === false)
    .map((task) => task.taskId);
  const remediationSceneIds = input.scenes
    .filter((scene) => scene.remediationCompletedCount > 0)
    .map((scene) => scene.sceneId);
  const recommendations: z.infer<typeof teacherReportRecommendationSchema>[] = [];

  if (input.status === "not_started") {
    const firstSceneId = input.scenes[0]?.sceneId;
    recommendations.push({
      code: "MODULE_NOT_STARTED",
      moduleId: input.moduleId,
      ...(firstSceneId ? { sceneIds: [firstSceneId] } : {}),
      message: firstSceneId
        ? `从${firstSceneId}进入本模块，完成首个场景的史料研读与任务。`
        : "进入本模块并完成首个场景的史料研读与任务。",
    });
    return recommendations;
  }
  if (incompleteSceneIds.length > 0) {
    recommendations.push({
      code: "SCENES_INCOMPLETE",
      moduleId: input.moduleId,
      sceneIds: incompleteSceneIds,
      message: `继续完成尚未闭环的场景：${incompleteSceneIds.join("、")}。`,
    });
  }
  if (unattemptedTaskIds.length > 0) {
    recommendations.push({
      code: "TASKS_UNATTEMPTED",
      moduleId: input.moduleId,
      taskIds: unattemptedTaskIds,
      message: `完成尚未产生正式作答记录的任务：${unattemptedTaskIds.join("、")}。`,
    });
  }
  if (notPassedTaskIds.length > 0) {
    recommendations.push({
      code: "TASKS_NOT_PASSED",
      moduleId: input.moduleId,
      taskIds: notPassedTaskIds,
      message: `回看任务反馈与对应史料后重试：${notPassedTaskIds.join("、")}。`,
    });
  }
  if (firstAttemptReviewTaskIds.length > 0) {
    recommendations.push({
      code: "FIRST_ATTEMPT_REVIEW",
      moduleId: input.moduleId,
      taskIds: firstAttemptReviewTaskIds,
      message: `复查首答需要辨析的任务及其反馈：${firstAttemptReviewTaskIds.join("、")}。`,
    });
  }
  if (remediationSceneIds.length > 0) {
    recommendations.push({
      code: "REMEDIATION_REVIEW",
      moduleId: input.moduleId,
      sceneIds: remediationSceneIds,
      message: `结合已完成的补救记录复述关键证据关系：${remediationSceneIds.join("、")}。`,
    });
  }
  if (recommendations.length === 0) {
    recommendations.push({
      code: "EVIDENCE_REHEARSAL",
      moduleId: input.moduleId,
      sceneIds: input.scenes.map((scene) => scene.sceneId),
      message: "本模块场景与任务记录已闭环，可结合代表性史料复述证据与结论的联系。",
    });
  }
  return recommendations.slice(0, 6);
}

export function buildTeacherLearnerReport(
  input: BuildTeacherLearnerReportInput,
): z.infer<typeof teacherLearnerReportSchema> {
  const taskFactById = new Map(input.taskFacts.map((task) => [task.taskId, task]));
  const sceneFactById = new Map(input.sceneFacts.map((scene) => [scene.sceneId, scene]));
  const taskCatalogByScene = new Map<string, TeacherReportTaskCatalogInput[]>();
  for (const task of input.taskCatalog) {
    const current = taskCatalogByScene.get(task.sceneId) ?? [];
    current.push(task);
    taskCatalogByScene.set(task.sceneId, current);
  }

  const modules = Array.from({ length: 6 }, (_, index) => `M${index + 1}`).map((moduleId) => {
    const moduleSceneIds = SCENE_IDS.filter((sceneId) => sceneId.startsWith(moduleId));
    const scenes = moduleSceneIds.map((sceneId) => {
      const fact = sceneFactById.get(sceneId);
      const tasks = (taskCatalogByScene.get(sceneId) ?? []).map((task) => {
        const taskFact = taskFactById.get(task.taskId);
        return {
          taskId: task.taskId,
          sceneId,
          attempted: taskFact !== undefined,
          passed: taskFact?.passed ?? false,
          attemptCount: taskFact?.attemptCount ?? 0,
          remediationStarted: taskFact?.remediationStarted ?? false,
          remediationCompleted: taskFact?.remediationCompleted ?? false,
          ...(taskFact?.firstAttemptCorrect !== undefined
            ? { firstAttemptCorrect: taskFact.firstAttemptCorrect }
            : {}),
        };
      });
      const attemptedTaskCount = tasks.filter((task) => task.attempted).length;
      const completed = fact?.completed ?? false;
      const eventCount = fact?.eventCount ?? 0;
      const started = completed || eventCount > 0 || attemptedTaskCount > 0;
      return {
        sceneId,
        status: reportStatus(started, completed),
        eventCount,
        completed,
        remediationStartedCount: fact?.remediationStartedCount ?? 0,
        remediationCompletedCount: fact?.remediationCompletedCount ?? 0,
        sourceViewCount: fact?.sourceViewCount ?? 0,
        contentViewCount: fact?.contentViewCount ?? 0,
        taskCount: tasks.length,
        attemptedTaskCount,
        passedTaskCount: tasks.filter((task) => task.passed).length,
        firstAttemptRecordedCount: tasks.filter((task) => task.firstAttemptCorrect !== undefined)
          .length,
        firstAttemptCorrectCount: tasks.filter((task) => task.firstAttemptCorrect === true).length,
        retriedTaskCount: tasks.filter((task) => task.attemptCount > 1).length,
        retryCount: tasks.reduce((sum, task) => sum + Math.max(0, task.attemptCount - 1), 0),
        attemptCount: tasks.reduce((sum, task) => sum + task.attemptCount, 0),
        tasks,
      };
    });
    const completedSceneCount = scenes.filter((scene) => scene.completed).length;
    const attemptedTaskCount = scenes.reduce((sum, scene) => sum + scene.attemptedTaskCount, 0);
    const status = reportStatus(
      scenes.some((scene) => scene.status !== "not_started"),
      completedSceneCount === scenes.length,
    );
    const base = {
      moduleId,
      status,
      completed: status === "completed",
      completedSceneCount,
      sceneCount: scenes.length,
      attemptedTaskCount,
      taskCount: scenes.reduce((sum, scene) => sum + scene.taskCount, 0),
      passedTaskCount: scenes.reduce((sum, scene) => sum + scene.passedTaskCount, 0),
      firstAttemptRecordedCount: scenes.reduce(
        (sum, scene) => sum + scene.firstAttemptRecordedCount,
        0,
      ),
      firstAttemptCorrectCount: scenes.reduce(
        (sum, scene) => sum + scene.firstAttemptCorrectCount,
        0,
      ),
      retriedTaskCount: scenes.reduce((sum, scene) => sum + scene.retriedTaskCount, 0),
      retryCount: scenes.reduce((sum, scene) => sum + scene.retryCount, 0),
      attemptCount: scenes.reduce((sum, scene) => sum + scene.attemptCount, 0),
      remediationStartedCount: scenes.reduce(
        (sum, scene) => sum + scene.remediationStartedCount,
        0,
      ),
      remediationCompletedCount: scenes.reduce(
        (sum, scene) => sum + scene.remediationCompletedCount,
        0,
      ),
      sourceViewCount: scenes.reduce((sum, scene) => sum + scene.sourceViewCount, 0),
      contentViewCount: scenes.reduce((sum, scene) => sum + scene.contentViewCount, 0),
      scenes,
    };
    return { ...base, recommendations: moduleRecommendations(base) };
  });

  const completedModuleCount = modules.filter((module) => module.completed).length;
  const completedSceneCount = modules.reduce((sum, module) => sum + module.completedSceneCount, 0);
  const attemptedTaskCount = modules.reduce((sum, module) => sum + module.attemptedTaskCount, 0);
  const overallRecommendations = modules.flatMap((module) => {
    const actionable = module.recommendations.find(
      (recommendation) => recommendation.code !== "EVIDENCE_REHEARSAL",
    );
    return actionable ? [actionable] : [];
  });
  if (overallRecommendations.length === 0) {
    overallRecommendations.push({
      code: "EVIDENCE_REHEARSAL",
      message: "六模块场景记录均已闭环，可按模块证据卡复核代表性史料与核心结论的联系。",
    });
  }

  return teacherLearnerReportSchema.parse({
    basis: input.basis,
    scope: input.scope,
    modules,
    overall: {
      status: reportStatus(
        modules.some((module) => module.status !== "not_started"),
        completedModuleCount === 6,
      ),
      completed: completedModuleCount === 6,
      completedModuleCount,
      moduleCount: 6,
      completedSceneCount,
      sceneCount: 18,
      attemptedTaskCount,
      taskCount: modules.reduce((sum, module) => sum + module.taskCount, 0),
      passedTaskCount: modules.reduce((sum, module) => sum + module.passedTaskCount, 0),
      firstAttemptRecordedCount: modules.reduce(
        (sum, module) => sum + module.firstAttemptRecordedCount,
        0,
      ),
      firstAttemptCorrectCount: modules.reduce(
        (sum, module) => sum + module.firstAttemptCorrectCount,
        0,
      ),
      retriedTaskCount: modules.reduce((sum, module) => sum + module.retriedTaskCount, 0),
      retryCount: modules.reduce((sum, module) => sum + module.retryCount, 0),
      attemptCount: modules.reduce((sum, module) => sum + module.attemptCount, 0),
      remediationStartedCount: modules.reduce(
        (sum, module) => sum + module.remediationStartedCount,
        0,
      ),
      remediationCompletedCount: modules.reduce(
        (sum, module) => sum + module.remediationCompletedCount,
        0,
      ),
      sourceViewCount: modules.reduce((sum, module) => sum + module.sourceViewCount, 0),
      contentViewCount: modules.reduce((sum, module) => sum + module.contentViewCount, 0),
      recommendations: overallRecommendations.slice(0, 6),
    },
  });
}

export const classroomLearnerResultSchema = z
  .object({
    learnerId: uuidSchema,
    profile: z
      .object({
        name: z.string().trim().min(1).max(API_LIMITS.name),
        major: z.string().trim().min(1).max(API_LIMITS.major),
        studentId: z.string().trim().min(1).max(API_LIMITS.studentId),
      })
      .strict(),
    process: z
      .object({
        sessionCount: z.number().int().min(0),
        eventCount: z.number().int().min(0),
        archiveOpenedCount: z.number().int().min(0),
        remediationCompletedCount: z.number().int().min(0),
        totalDurationMs: z.number().int().min(0),
        latestActivityAt: timestampSchema.optional(),
      })
      .strict(),
    summary: z
      .object({
        attemptedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
        passedTaskCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
        firstAttemptCorrectCount: z.number().int().min(0).max(API_LIMITS.formalTasks),
        totalAttemptCount: z.number().int().min(0),
        averageBestScore: z.number().min(0).max(1),
        earnedPoints: z.number().min(0).optional(),
        possiblePoints: z.number().min(1).optional(),
      })
      .strict()
      .superRefine((summary, context) => {
        if ((summary.earnedPoints === undefined) !== (summary.possiblePoints === undefined)) {
          context.addIssue({
            code: "custom",
            message: "Assignment points must include both earnedPoints and possiblePoints.",
          });
        }
        if (
          summary.earnedPoints !== undefined &&
          summary.possiblePoints !== undefined &&
          summary.earnedPoints > summary.possiblePoints
        ) {
          context.addIssue({
            code: "custom",
            path: ["earnedPoints"],
            message: "Earned points cannot exceed possible points.",
          });
        }
      }),
    taskResults: z.array(classroomTaskResultSchema).max(API_LIMITS.formalTasks),
    report: teacherLearnerReportSchema,
  })
  .strict();

export const classroomResultsResponseSchema = z
  .object({
    authority: z.literal("server"),
    generatedAt: timestampSchema,
    classroom: classroomSchema,
    assignment: assignmentSchema.optional(),
    learners: z.array(classroomLearnerResultSchema).max(100),
    nextCursor: uuidSchema.optional(),
  })
  .strict();

export type ClassroomStatus = z.infer<typeof classroomStatusSchema>;
export type CreateClassroomRequest = z.infer<typeof createClassroomRequestSchema>;
export type Classroom = z.infer<typeof classroomSchema>;
export type CreateClassroomResponse = z.infer<typeof createClassroomResponseSchema>;
export type ClassroomListResponse = z.infer<typeof classroomListResponseSchema>;
export type RotateInviteCodeResponse = z.infer<typeof rotateInviteCodeResponseSchema>;
export type PublicTaskCatalogItem = z.infer<typeof publicTaskCatalogItemSchema>;
export type TaskCatalogResponse = z.infer<typeof taskCatalogResponseSchema>;
export type AssignmentItem = z.infer<typeof assignmentItemSchema>;
export type CreateAssignmentDraftRequest = z.infer<typeof createAssignmentDraftRequestSchema>;
export type UpdateAssignmentDraftRequest = z.infer<typeof updateAssignmentDraftRequestSchema>;
export type Assignment = z.infer<typeof assignmentSchema>;
export type AssignmentListResponse = z.infer<typeof assignmentListResponseSchema>;
export type PublishAssignmentRequest = z.infer<typeof publishAssignmentRequestSchema>;
export type WithdrawAssignmentRequest = z.infer<typeof withdrawAssignmentRequestSchema>;
export type ClassroomResultsQuery = z.infer<typeof classroomResultsQuerySchema>;
export type ClassroomTaskResult = z.infer<typeof classroomTaskResultSchema>;
export type TeacherReportStatus = z.infer<typeof teacherReportStatusSchema>;
export type TeacherReportRecommendation = z.infer<typeof teacherReportRecommendationSchema>;
export type TeacherReportTask = z.infer<typeof teacherReportTaskSchema>;
export type TeacherReportScene = z.infer<typeof teacherReportSceneSchema>;
export type TeacherModuleReport = z.infer<typeof teacherModuleReportSchema>;
export type TeacherOverallReport = z.infer<typeof teacherOverallReportSchema>;
export type TeacherLearnerReport = z.infer<typeof teacherLearnerReportSchema>;
export type ClassroomLearnerResult = z.infer<typeof classroomLearnerResultSchema>;
export type ClassroomResultsResponse = z.infer<typeof classroomResultsResponseSchema>;
